import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  FolderTree,
  Users,
  Activity,
  Settings,
  LogOut,
  UserCircle,
  ExternalLink,
} from "lucide-react";
import { useEffect } from "react";
import { authApi } from "../../api";
import CubeLogo from "../../components/CubeLogo";

export default function AdminLayout() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["me"], queryFn: authApi.me, retry: false });

  useEffect(() => {
    if (!isLoading && !data?.user) navigate("/admin/login", { replace: true });
  }, [data, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center text-ink-500 text-[13px] bg-cream-100">
        Loading…
      </div>
    );
  }
  if (!data?.user) return null;

  const isAdmin = data.user.role === "admin";

  return (
    <div className="min-h-screen flex bg-cream-100">
      <aside className="w-60 bg-cream-50 border-r border-ink-200/70 flex flex-col shrink-0">
        <div className="px-4 h-16 flex items-center border-b border-ink-200/70">
          <div className="flex items-center gap-2.5">
            <CubeLogo size={24} />
            <span className="text-[14px] font-semibold tracking-tight text-ink-900">
              CORE <span className="text-ink-400 font-normal">Admin</span>
            </span>
          </div>
        </div>

        <nav className="flex-1 px-2.5 py-3.5 space-y-0.5">
          <NavItem to="/admin" end icon={<LayoutDashboard className="size-3.5" />}>Dashboard</NavItem>
          <NavItem to="/admin/departments" icon={<FolderTree className="size-3.5" />}>Departments</NavItem>
          {isAdmin && <NavItem to="/admin/users" icon={<Users className="size-3.5" />}>Users</NavItem>}
          {isAdmin && <NavItem to="/admin/audit" icon={<Activity className="size-3.5" />}>Audit log</NavItem>}
          {isAdmin && <NavItem to="/admin/settings" icon={<Settings className="size-3.5" />}>Settings</NavItem>}
          <NavItem to="/admin/account" icon={<UserCircle className="size-3.5" />}>My account</NavItem>

          <div className="pt-3 mt-3 border-t border-ink-200/70" />

          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-md text-[12.5px] text-ink-500 hover:bg-cream-200 hover:text-ink-900 transition-colors"
          >
            <ExternalLink className="size-3.5" /> Open viewer
          </a>
        </nav>

        <div className="border-t border-ink-200/70 p-3.5">
          <div className="flex items-center gap-2.5 mb-2.5">
            <div className="size-7 rounded-full bg-ink-900 grid place-items-center text-cream-50 text-[10.5px] font-semibold uppercase">
              {(data.user.display_name ?? data.user.username).slice(0, 1)}
            </div>
            <div className="min-w-0">
              <div className="text-[12.5px] font-medium truncate">
                {data.user.display_name ?? data.user.username}
              </div>
              <div className="text-[10.5px] text-ink-400 capitalize">{data.user.role}</div>
            </div>
          </div>
          <button
            onClick={async () => {
              await authApi.logout();
              navigate("/admin/login", { replace: true });
            }}
            className="btn-secondary btn-sm w-full"
          >
            <LogOut className="size-3" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}

function NavItem({
  to,
  end,
  icon,
  children,
}: {
  to: string;
  end?: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 rounded-md text-[12.5px] transition-colors ${
          isActive
            ? "bg-ink-900 text-cream-50"
            : "text-ink-700 hover:bg-cream-200 hover:text-ink-900"
        }`
      }
    >
      {icon}
      <span>{children}</span>
    </NavLink>
  );
}
